import { FlexColumn, FlexRow, StyledButton } from '@gorila-shared-ui/components';
import { OnChangeParams } from 'baseui/select';
import { Filter } from 'baseui/table';
import { memo, useMemo } from 'react';
import { useRecoilState } from 'recoil';
import { useStyles } from '../../../hooks/useStyles';
import EnhancedLabeledSelect from '../../ui/EnhancedLabeledSelect';
import { FLEET_HEALTH_EVENT_TYPE } from '../../../constants/fleetHealth';
import { fleetHealthClientState, fleetHealthConfigurationState, fleetHealthEventTypeState, fleetHealthSubClientState } from '../../../storage/fleetHealthStates';
import { FleetHealthRuleSelect } from '../../shared/selects/FleetHealthRuleSelect';
import { ClientSelect } from '../../shared/selects/ClientSelect';
import { SubClientSelect } from '../../shared/selects/SubClientSelect';

type Props = {
  resetFilters: () => void;
}
function FleetHealthRulesFilters({ resetFilters }: Readonly<Props>) {
  const { theme, css } = useStyles();
  const [selectType, setSelectType] = useRecoilState(fleetHealthEventTypeState);
  const [configuration, setConfiguration] = useRecoilState(fleetHealthConfigurationState);
  const [selectedClientId, setSelectedClientId] = useRecoilState(fleetHealthClientState);
  const [selectedSubClientId, setSelectedSubClientId] = useRecoilState(fleetHealthSubClientState);

  const eventTypeOptions = useMemo(() => {
    return [
      {
        id: '',
        label: 'Todos',
      },
      ...Object.keys(FLEET_HEALTH_EVENT_TYPE).map((statusKey) => ({
        id: statusKey,
        label: FLEET_HEALTH_EVENT_TYPE[statusKey as keyof typeof FLEET_HEALTH_EVENT_TYPE],
      })),
    ];
  }, [FLEET_HEALTH_EVENT_TYPE]);

  const onEventTypeChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectType(params.option.id as keyof typeof FLEET_HEALTH_EVENT_TYPE) : setSelectType(undefined);
  };

  const handleClientChange = (clientId?: string) => {
    if (clientId !== selectedClientId) {
      setSelectedClientId(clientId);
      setSelectedSubClientId(undefined);
    }
  };

  const isActive = useMemo(() => {
    if (selectType) {
      return true;
    }
    return false;
  }, [selectType]);

  return (
    <Filter
      active={isActive}
      overrides={{
        Heading: {
          component: () => <></>,
        },
        Footer: {
          component: () => {
            return (
              <FlexRow
                classNames={css({
                  justifyContent: 'end',
                  padding: theme.sizing.scale200,
                })}
              >
                <StyledButton
                  onClick={resetFilters}
                  size="mini"
                  kind="tertiary"
                >
                  Limpiar filtros
                </StyledButton>
              </FlexRow>
            );
          },
        },
        Content: {
          style: () => ({
            borderTop: 'none',
            minHeight: '220px',
            minWidth: '200px',
          }),
        },
      }}
    >
      <FlexColumn
        classNames={css({
          paddingTop: theme.sizing.scale500,
          paddingBottom: theme.sizing.scale500,
        })}
        gap={theme.sizing.scale200}
      >
        <ClientSelect
          selectedClientId={selectedClientId}
          onChangeClientId={handleClientChange}
          inline
        />
        <SubClientSelect
          selectedClientId={selectedClientId}
          selectedSubClientId={selectedSubClientId}
          onChangeSubClientId={setSelectedSubClientId}
          disabled={!selectedClientId}
          inline
        />
        <EnhancedLabeledSelect
          options={eventTypeOptions}
          label="Tipo:"
          value={[{id: selectType}]}
          onChange={onEventTypeChange}
          inline
          inset
        />
        <FleetHealthRuleSelect 
          label='Regla:'
          selectedRuleId={configuration}
          onChangeRuleId={setConfiguration}
          placeholder=''
          inline
        />
      </FlexColumn>
    </Filter>
  );
}

export default memo(FleetHealthRulesFilters);
